/** Archivo de calendario (.ics, iCalendar RFC 5545) armado en el navegador, sin pasar por el servidor. */

export interface IcsEvent {
  title: string;
  startsAt: string | Date;
  endsAt: string | Date;
  location?: string | null;
  url?: string | null;
  description?: string | null;
  /** Identificador estable del evento (así, al importarlo dos veces, el calendario lo actualiza). */
  uid?: string;
}

/** Fecha en UTC con el formato de iCalendar: 20261102T183000Z. */
export const icsDate = (d: string | Date) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

/** Escapa el texto según RFC 5545 (barra, punto y coma, coma y saltos de línea). */
const escape = (text: string) => text.replace(/\\/g, '\\\\').replace(/;/g, '\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Dobla las líneas largas a 75 octetos (UTF-8), como pide el estándar. */
function fold(line: string): string {
  const out: string[] = [];
  let current = '';
  let bytes = 0;
  for (const ch of line) {
    const size = new TextEncoder().encode(ch).length;
    if (bytes + size > (out.length ? 74 : 75)) {
      out.push(current);
      current = '';
      bytes = 0;
    }
    current += ch;
    bytes += size;
  }
  out.push(current);
  return out.join('\r\n ');
}

/** Contenido del .ics con un solo evento. */
export function buildIcs(event: IcsEvent, now: Date = new Date()): string {
  const description = [event.description, event.url].filter(Boolean).join('\n\n');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//KEORA//Calendario//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.uid ?? `${icsDate(event.startsAt)}-${Math.random().toString(36).slice(2)}`}@mykeora.com`,
    `DTSTAMP:${icsDate(now)}`,
    `DTSTART:${icsDate(event.startsAt)}`,
    `DTEND:${icsDate(event.endsAt)}`,
    `SUMMARY:${escape(event.title)}`,
    event.location ? `LOCATION:${escape(event.location)}` : null,
    description ? `DESCRIPTION:${escape(description)}` : null,
    event.url ? `URL:${event.url}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter((l): l is string => l !== null);
  return lines.map(fold).join('\r\n') + '\r\n';
}

/** Nombre de archivo sencillo a partir del título: «Culto de jóvenes» → culto-de-jovenes.ics. */
export const icsFileName = (title: string) =>
  `${title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'evento'}.ics`;

/** Descarga el .ics (en el celular, el sistema ofrece abrirlo con su calendario). */
export function downloadIcs(event: IcsEvent): void {
  const blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = icsFileName(event.title);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
